import { z } from 'zod';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para análisis profundo (M2.13).
 *
 * Endpoints:
 *   - GET /api/analytics/discipline-evolution?discipline=X&days=N
 *   - GET /api/analytics/zones-by-month?month=YYYY-MM
 *   - GET /api/analytics/period-compare?periodA=fromISO,toISO&periodB=...
 *
 * Sigue el mismo patrón que `lib/load.ts` y `lib/stats.ts`: schemas Zod,
 * fetch propio con mapeo de errores via ApiError. Sin librerías externas.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Schemas: discipline-evolution
// ---------------------------------------------------------------------------

export const EvolutionWeekSchema = z.object({
  weekStartISO: z.string(),
  weekEndISO: z.string(),
  sessions: z.number().int().nonnegative(),
  hours: z.number().nonnegative(),
  tss: z.number().nonnegative(),
  distanceM: z.number().nonnegative(),
  elevationGainM: z.number().nonnegative(),
  avgPaceSecPerKm: z.number().nullable(),
  avgPowerW: z.number().nullable(),
  avgHr: z.number().nullable(),
});
export type EvolutionWeek = z.infer<typeof EvolutionWeekSchema>;

export const DisciplineEvolutionResponseSchema = z.object({
  discipline: z.string(),
  days: z.number().int().positive(),
  weeks: z.array(EvolutionWeekSchema),
});
export type DisciplineEvolutionResponse = z.infer<typeof DisciplineEvolutionResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: zones-by-month
// ---------------------------------------------------------------------------

export const ZoneSlotSchema = z.object({
  zone: z.string(),
  minutes: z.number().nonnegative(),
  percent: z.number().min(0).max(100),
});
export type ZoneSlot = z.infer<typeof ZoneSlotSchema>;

export const DisciplineZoneBlockSchema = z.object({
  discipline: z.string(),
  zones: z.array(ZoneSlotSchema),
  previousMonthZones: z.array(ZoneSlotSchema),
});
export type DisciplineZoneBlock = z.infer<typeof DisciplineZoneBlockSchema>;

export const ZonesByMonthResponseSchema = z.object({
  month: z.string(),
  previousMonth: z.string(),
  byDiscipline: z.array(DisciplineZoneBlockSchema),
});
export type ZonesByMonthResponse = z.infer<typeof ZonesByMonthResponseSchema>;

// ---------------------------------------------------------------------------
// Schemas: period-compare
// ---------------------------------------------------------------------------

export const PeriodTotalsSchema = z.object({
  fromISO: z.string(),
  toISO: z.string(),
  sessions: z.number().int().nonnegative(),
  hours: z.number().nonnegative(),
  tss: z.number().nonnegative(),
  distanceSwimM: z.number().nonnegative(),
  distanceBikeM: z.number().nonnegative(),
  distanceRunM: z.number().nonnegative(),
  distanceTotalM: z.number().nonnegative(),
  elevationGainM: z.number().nonnegative(),
  calories: z.number().nonnegative(),
});
export type PeriodTotals = z.infer<typeof PeriodTotalsSchema>;

export const PeriodCompareResponseSchema = z.object({
  a: PeriodTotalsSchema,
  b: PeriodTotalsSchema,
});
export type PeriodCompareResponse = z.infer<typeof PeriodCompareResponseSchema>;

// ---------------------------------------------------------------------------
// HTTP helper (gemelo a lib/load.ts)
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function getDisciplineEvolution(
  discipline: string,
  days: number,
): Promise<DisciplineEvolutionResponse> {
  const qs = `?discipline=${encodeURIComponent(discipline)}&days=${encodeURIComponent(String(days))}`;
  return getJson(`/api/analytics/discipline-evolution${qs}`, DisciplineEvolutionResponseSchema);
}

export function getZonesByMonth(month: string): Promise<ZonesByMonthResponse> {
  return getJson(
    `/api/analytics/zones-by-month?month=${encodeURIComponent(month)}`,
    ZonesByMonthResponseSchema,
  );
}

export function getPeriodCompare(
  periodA: { fromISO: string; toISO: string },
  periodB: { fromISO: string; toISO: string },
): Promise<PeriodCompareResponse> {
  const a = `${periodA.fromISO},${periodA.toISO}`;
  const b = `${periodB.fromISO},${periodB.toISO}`;
  return getJson(
    `/api/analytics/period-compare?periodA=${encodeURIComponent(a)}&periodB=${encodeURIComponent(b)}`,
    PeriodCompareResponseSchema,
  );
}

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export const ANALYTICS_QUERY_KEY = ['analytics'] as const;
export const ANALYTICS_EVOLUTION_KEY = ['analytics', 'discipline-evolution'] as const;
export const ANALYTICS_ZONES_KEY = ['analytics', 'zones-by-month'] as const;
export const ANALYTICS_PERIOD_KEY = ['analytics', 'period-compare'] as const;

export function useDisciplineEvolution(
  discipline: string,
  days: number,
): UseQueryResult<DisciplineEvolutionResponse, Error> {
  return useQuery<DisciplineEvolutionResponse, Error>({
    queryKey: [...ANALYTICS_EVOLUTION_KEY, discipline, days],
    queryFn: () => getDisciplineEvolution(discipline, days),
    staleTime: 60_000,
    retry: 1,
  });
}

export function useZonesByMonth(
  month: string,
): UseQueryResult<ZonesByMonthResponse, Error> {
  return useQuery<ZonesByMonthResponse, Error>({
    queryKey: [...ANALYTICS_ZONES_KEY, month],
    queryFn: () => getZonesByMonth(month),
    staleTime: 60_000,
    retry: 1,
  });
}

export function usePeriodCompare(
  periodA: { fromISO: string; toISO: string },
  periodB: { fromISO: string; toISO: string },
  enabled: boolean = true,
): UseQueryResult<PeriodCompareResponse, Error> {
  return useQuery<PeriodCompareResponse, Error>({
    queryKey: [...ANALYTICS_PERIOD_KEY, periodA.fromISO, periodA.toISO, periodB.fromISO, periodB.toISO],
    queryFn: () => getPeriodCompare(periodA, periodB),
    staleTime: 60_000,
    retry: 1,
    enabled,
  });
}
